export type TaskStatus = "open" | "done";
export type TaskViewer = "admin" | "creator" | "client" | "client-viewer";

/** Relative to the task's client: a member of the agency, or a contact
 * with access to this client (TD-007). */
export type TaskAssigneeKind = "team" | "client";

export interface TaskAssignee {
  id: string;
  name: string | null;
  /** Absent for a team member in portal payloads: clients never get the
   * team's addresses (TD-103). */
  email?: string;
  kind: TaskAssigneeKind;
}

export type TaskAssigneeOption = TaskAssignee;

/** A person on a task: its owner or one of its tags (TD-103). */
export type TaskPerson = TaskAssignee;

export interface AssignablePeopleResponse {
  people: TaskAssigneeOption[];
}

/** A task's links live in entity_links (TD-007), read through TaskDetail. */
export interface Task {
  id: string;
  clientId: string;
  title: string;
  description: string | null;
  assigneeId: string | null;
  assignee: TaskAssignee | null;
  status: TaskStatus;
  position: number;
  doneAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  /** The tagged people, owner left out, team first, then by name (TD-103). */
  people: TaskPerson[];
  /** The client sees it in the portal: its owner or a tag is a client contact. */
  clientVisible: boolean;
}

export interface TasksResponse {
  tasks: Task[];
  count: number;
}

/** The item types a task links to (TD-007). Wire values, never re-cased. */
export type TaskLinkType = "idea" | "brief" | "post" | "calendar_event";

/** The registry's extras: `{status, postType}` for a post, `{}` otherwise. */
export interface TaskLinkMeta {
  status?: string;
  postType?: string;
}

/** A direct link. `id` is the task → item entity_links row, the one
 * `DELETE /api/tasks/<id>/links/<link_id>` takes. */
export interface TaskLink {
  id: string;
  type: TaskLinkType;
  targetId: string;
  title: string;
  meta: TaskLinkMeta;
}

/** The direct link's item a carried link came through. */
export interface TaskLinkVia {
  type: TaskLinkType;
  targetId: string;
  title: string;
}

/** An item one step from a direct link (TD-096), computed on read. */
export interface TaskCarriedLink {
  type: TaskLinkType;
  targetId: string;
  title: string;
  meta: TaskLinkMeta;
  via: TaskLinkVia;
}

/** `GET /api/tasks/<id>`. */
export interface TaskDetail {
  task: Task;
  links: TaskLink[];
  carried: TaskCarriedLink[];
}

/** An item to link: `POST /api/tasks/<id>/links` and a create's `links`. */
export interface TaskLinkRef {
  type: TaskLinkType;
  targetId: string;
}

export interface TaskLinkResponse {
  link: TaskLink;
}

/** One picker row from `GET /api/clients/<id>/task-link-options`. */
export interface TaskLinkOption {
  type: TaskLinkType;
  id: string;
  title: string;
  subtitle: string;
}

export interface TaskLinkOptionsResponse {
  options: TaskLinkOption[];
}

/** The related item a via group's tasks came through. */
export interface TaskViaItem {
  type: TaskLinkType;
  id: string;
  title: string;
}

export interface TaskViaGroup {
  item: TaskViaItem;
  tasks: Task[];
}

/** An item's task list (the idea checklist, the brief and portal shot
 * lists): its own tasks, then one group per related item its other tasks
 * came through (DD-052). */
export interface TaskList {
  tasks: Task[];
  via: TaskViaGroup[];
}

export const EMPTY_TASK_LIST: TaskList = { tasks: [], via: [] };

/** `author` is null once the person who wrote the note is deleted (TD-095). */
export interface TaskNoteAuthor {
  id: string;
  name: string | null;
}

export interface TaskNote {
  id: string;
  body: string;
  author: TaskNoteAuthor | null;
  createdAt: string;
}

export interface TaskNotesResponse {
  notes: TaskNote[];
}

export interface TaskNoteResponse {
  note: TaskNote;
}

export interface CreateTaskRequest {
  title: string;
  description?: string | null;
  assigneeId?: string | null;
  /** Linked in the same commit as the task. */
  links?: TaskLinkRef[];
  /** The tags. The server tags the creator too (DD-053). */
  personIds?: string[];
}

export interface UpdateTaskRequest {
  title?: string;
  description?: string | null;
  status?: TaskStatus;
  assigneeId?: string | null;
  /** Replaces the tags, sent only when they changed. The portal's replaces only the client's own people (TD-103). */
  personIds?: string[];
  /** Moves the task to another client of the agency: its links go, the team stays (DD-049). */
  clientId?: string;
}

/** `POST /api/tasks/reorder`: the order of one item's own tasks. */
export interface ReorderTasksRequest {
  targetType: TaskLinkType;
  targetId: string;
  taskIds: string[];
}

export interface AgencyTaskGroup {
  client: { id: string; name: string };
  tasks: Task[];
}

export interface AgencyTasksResponse {
  groups: AgencyTaskGroup[];
  count: number;
}

/** `assignee` is a person id, "team" for unassigned tasks, or null for
 * everyone. */
export interface AgencyTaskFilters {
  status: TaskStatus;
  assignee: string | null;
}

// Query keys factory
export const taskKeys = {
  all: ["tasks"] as const,
  entity: (entityType: string, entityId: string) =>
    [...taskKeys.all, "entity", entityType, entityId] as const,
  standalone: (clientId: string) =>
    [...taskKeys.all, "standalone", clientId] as const,
  shotList: (briefId: string) =>
    [...taskKeys.all, "shot-list", briefId] as const,
  /** The agency Tasks tab (TD-094). Under `all`, so every task mutation and
   * a person rename refetch it. */
  agency: (filters: AgencyTaskFilters) =>
    [...taskKeys.all, "agency", filters] as const,
  /** The client portal's Tasks list (DD-053). Under `all`, so a save or a
   * check-off refetches it and a task handed to the team leaves. */
  portal: (clientId: string, status: TaskStatus) =>
    [...taskKeys.all, "portal", clientId, status] as const,
  /** One task with its links, for the drawer. */
  detail: (taskId: string) => [...taskKeys.all, "detail", taskId] as const,
  /** One task's notes (DD-050). A person rename refreshes author names. */
  notes: (taskId: string) => [...taskKeys.all, "notes", taskId] as const,
  /** The link picker's search over one type of a client's items. */
  linkOptions: (clientId: string, type: TaskLinkType, q: string) =>
    [...taskKeys.all, "link-options", clientId, type, q] as const,
};

/** People a task on a client can be assigned to. A rename invalidates
 * ``all``, which prefix-matches every client's list. */
export const assignablePeopleKeys = {
  all: ["assignable-people"] as const,
  client: (clientId: string) => [...assignablePeopleKeys.all, clientId] as const,
};
